// ─────────────────────────────────────────────────────────────────────────────
// IgnoreCodeService
// ─────────────────────────────────────────────────────────────────────────────
// HTTP service for ignored code operations.
// Endpoints: /ai/kb/ignore-codes for all ignore code operations.
// Supports manual create + edit (source='manual'); order-sourced entries remain
// read-only on the backend.
// ─────────────────────────────────────────────────────────────────────────────

import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { skipErrorToast } from 'src/app/core/http/error-toast.token';
import { APIResponse } from 'src/app/core/models/api-response';
import { IPageCount } from 'src/app/shared/models/page.model';
import { environment } from 'src/environments/environment';
import { ICreateIgnoreCodePayload, IgnoreCodeFilters, IIgnoreCode, IUpdateIgnoreCodePayload } from '../models/ignore-code.models';

@Injectable({
  providedIn: 'root',
})
export class IgnoreCodeService {
  constructor(private http: HttpClient) {}

  // ── List ───────────────────────────────────────────────────────────────────

  public listIgnoreCodes(
    page: IPageCount,
    key?: string,
    filters?: IgnoreCodeFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<APIResponse<IIgnoreCode>> {
    let params = new HttpParams().set('page', page.number).set('limit', page.size);

    if (key) {
      params = params.set('key', key);
    }
    if (sortBy) {
      params = params.set('sort_by', sortBy);
    }
    if (sortMode) {
      params = params.set('sort_mode', sortMode);
    }
    if (filters) {
      if (filters.customer_id != null) {
        params = params.set('customer_id', filters.customer_id);
      }
      if (filters.create_date_from) {
        params = params.set('create_date_from', filters.create_date_from);
      }
      if (filters.create_date_to) {
        params = params.set('create_date_to', filters.create_date_to);
      }
    }

    return this.http.get<APIResponse<IIgnoreCode>>(`${environment.BASE_URL_API}ai/kb/ignore-codes`, { params });
  }

  // ── Create ─────────────────────────────────────────────────────────────────

  public createIgnoreCode(payload: ICreateIgnoreCodePayload): Observable<APIResponse<IIgnoreCode>> {
    return this.http.post<APIResponse<IIgnoreCode>>(`${environment.BASE_URL_API}ai/kb/ignore-codes`, payload);
  }

  // ── Update ─────────────────────────────────────────────────────────────────

  public updateIgnoreCode(id: number, payload: IUpdateIgnoreCodePayload): Observable<APIResponse<IIgnoreCode>> {
    return this.http.post<APIResponse<IIgnoreCode>>(`${environment.BASE_URL_API}ai/kb/ignore-codes/${id}`, payload);
  }

  // ── Delete ─────────────────────────────────────────────────────────────────

  public deleteIgnoreCode(id: number): Observable<APIResponse<IIgnoreCode>> {
    return this.http.delete<APIResponse<IIgnoreCode>>(`${environment.BASE_URL_API}ai/kb/ignore-codes/${id}`);
  }

  // ── Bulk delete ────────────────────────────────────────────────────────────

  public bulkDeleteIgnoreCodes(ids: number[]): Observable<APIResponse<IIgnoreCode>> {
    return this.http.post<APIResponse<IIgnoreCode>>(`${environment.BASE_URL_API}ai/kb/ignore-codes/bulk/delete`, { ids });
  }

  // ── Export ──────────────────────────────────────────────────────────────────

  public exportIgnoreCodes(
    format: 'csv' | 'xlsx' = 'xlsx',
    key?: string,
    filters?: IgnoreCodeFilters,
    sortBy?: string,
    sortMode?: 'asc' | 'desc',
  ): Observable<Blob> {
    let params = new HttpParams().set('format', format);

    if (key) params = params.set('key', key);
    if (sortBy) params = params.set('sort_by', sortBy);
    if (sortMode) params = params.set('sort_mode', sortMode);
    if (filters) {
      if (filters.customer_id != null) params = params.set('customer_id', filters.customer_id);
      if (filters.create_date_from) params = params.set('create_date_from', filters.create_date_from);
      if (filters.create_date_to) params = params.set('create_date_to', filters.create_date_to);
    }

    return this.http.get(`${environment.BASE_URL_API}ai/kb/ignore-codes/export`, {
      params,
      responseType: 'blob',
      // Blob responses can't carry a parseable error body, so the global
      // interceptor would only ever show a generic toast — the component
      // surfaces a specific "export failed" message instead.
      context: skipErrorToast(),
    });
  }
}
